import { useCallback, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Shared by every admin screen that tracks who has been called (Follow-ups,
// OTS Fee Receipts, Move-outs & Dues). The server sends each row a
// `followUp` summary; these helpers colour, filter and sort by it.

export const STATE_META = {
  not_contacted: { label: "Not contacted", bg: "#fee2e2", color: "#991b1b", border: "#ef4444", tint: "#fffafa" },
  due: { label: "Follow up again", bg: "#fef3c7", color: "#92400e", border: "#f59e0b", tint: "#fffdf5" },
  contacted: { label: "Contacted", bg: "#dcfce7", color: "#166534", border: "#22c55e", tint: "#f7fef9" },
  closed: { label: "Done", bg: "#e5e7eb", color: "#374151", border: "#9ca3af", tint: "#fafafa" },
};

export const OUTCOMES = [
  { key: "reached", label: "Spoke to them" },
  { key: "no_answer", label: "No answer" },
  { key: "promised", label: "Promised to act" },
  { key: "closed", label: "Done — no more follow-up" },
];

export const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.key, o.label]));

export const PARTY_LABEL = { tenant: "Tenant", owner: "Owner" };

const RANK = { not_contacted: 0, due: 1, contacted: 2, closed: 3 };

export const stateOf = (row) => row?.followUp?.state || "not_contacted";

export const FILTERS = [
  { key: "all", label: "All" },
  { key: "todo", label: "To call" },
  { key: "not_contacted", label: "Not contacted" },
  { key: "due", label: "Follow up again" },
  { key: "contacted", label: "Contacted" },
  { key: "closed", label: "Done" },
];

// "To call" = everything that still needs someone to pick up the phone.
export const matchesFilter = (row, filter) => {
  const state = stateOf(row);
  if (filter === "all") return true;
  if (filter === "todo") return state === "not_contacted" || state === "due";
  return state === filter;
};

export const filterCounts = (rows) => {
  const counts = {};
  FILTERS.forEach((f) => {
    counts[f.key] = rows.filter((r) => matchesFilter(r, f.key)).length;
  });
  return counts;
};

export const needsCall = (row) => matchesFilter(row, "todo");

const SEARCH_FIELDS = [
  "tenantName", "tenantEmail", "tenantPhone",
  "landlordName", "landlordEmail", "landlordPhone",
  "flatName", "flatCity",
];

export const matchesSearch = (row, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return SEARCH_FIELDS.some((f) => String(row[f] || "").toLowerCase().includes(q));
};

// Not-yet-called first, then overdue follow-ups, then the rest — the order an
// admin would work through the list. Stable within a group, so the screen's
// own ordering (longest waiting first) is kept.
export const sortByUrgency = (rows) =>
  rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => RANK[stateOf(a.row)] - RANK[stateOf(b.row)] || a.i - b.i)
    .map(({ row }) => row);

export const rowSx = (row) => {
  const meta = STATE_META[stateOf(row)];
  return { borderLeft: `4px solid ${meta.border}`, bgcolor: meta.tint };
};

const authHeaders = () => {
  const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
  return { Authorization: `Bearer ${token}` };
};

export const fetchFollowUp = async (scope, subjectId) => {
  const { data } = await axios.get(`${BASE_URL}/admin/follow-ups/${scope}/${encodeURIComponent(subjectId)}`, {
    headers: authHeaders(),
  });
  return data;
};

export const saveFollowUp = async (payload) => {
  const { data } = await axios.post(`${BASE_URL}/admin/follow-ups`, payload, { headers: authHeaders() });
  return data;
};

// A screen keeps the summaries the dialog just saved here and lays them over
// its rows, so a row turns green the moment the call is logged without
// reloading the whole list.
export const useFollowUpOverrides = () => {
  const [overrides, setOverrides] = useState({});
  const record = useCallback(
    (followUp) =>
      setOverrides((o) => ({ ...o, [`${followUp.scope}|${followUp.subjectId}`]: followUp })),
    []
  );
  const apply = useCallback(
    (row) => {
      const fu = row.followUp && overrides[`${row.followUp.scope}|${row.followUp.subjectId}`];
      return fu ? { ...row, followUp: fu } : row;
    },
    [overrides]
  );
  return { record, apply };
};
